import { AsyncPipe, KeyValuePipe, Location, NgClass } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { AngularFireAnalytics } from "@angular/fire/compat/analytics";
import { DocumentData } from "@angular/fire/firestore";
import { MatDialog } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { ActivatedRoute, Data, Router } from "@angular/router";
import { isMobile } from "@core/utility.service";
import { GameChapterBadge } from "@interfaces/skill-game";
import { IonicModule } from "@ionic/angular";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Subject, Subscription, from, interval, of } from "rxjs";
import { catchError, map, switchMap, takeUntil } from "rxjs/operators";
import { bounceIn } from "src/app/animations/custom.animation";
import { CommonVariable } from "src/app/app.constants";
import { WindowsGamePlayPopupComponent } from "src/app/components/windows-game-play-popup/windows-game-play-popup.component";
import { GTMSegmentService } from "src/app/services/gtm-segment.service";
import { InProgressAnimationComponent } from "../../../components/in-progress-animation/in-progress-animation.component";
import { KeysPipe } from "../../../pipe/keys";
import { GamesFeedSampleService } from "../../games-feed-sample/games-feed-sample.service";
import { GamesRepositoryService } from "../games-repository.service";
import { GameDatasService } from "../services/game-data.service";
import { GamePlayBaseComponent } from "../shared/game-play-base.component";

const zeroValue = 0;
const oneValue = 1;

@Component({
  selector: "app-skill-game",
  templateUrl: "./skill-game.component.html",
  styleUrls: ["./skill-game.component.scss"],
  animations: [bounceIn],
  standalone: true,
  imports: [
    IonicModule,
    NgClass,
    MatProgressSpinner,
    MatIcon,
    InProgressAnimationComponent,
    AsyncPipe,
    KeyValuePipe,
    KeysPipe,
    TranslatePipe,
  ],
})
export class SkillGameComponent extends GamePlayBaseComponent implements OnInit {
  public languageCode: string;
  protected hidePrizeHeading: number;
  gameTotalPrize: string;
  gameTopPrize: string;
  public flashCost: Data;
  public userDataSubscription: Subscription;
  flashSaleEnabled: boolean;
  flashSalesExpiryDate: Data;
  salesCost: number | string;
  compareFn = (a, b) => {
    return a.key.localeCompare(b.key);
  };
  gameInfo$ =
    Object.keys(this.route?.snapshot?.queryParams).length > zeroValue
      ? this.gameDatasService.getY2PGameData()
      : this.gameDatasService.getGameInfo(this.route.snapshot.params.id, 'skill').pipe(
        switchMap((res: DocumentData[]) => {
          const skillGame = res.map((element) => element?.data()); // modular SDK: .data()
          const preferredCurrency = this.preferredCurrency ?? CommonVariable.preferredCurrency;
          if (skillGame && skillGame[oneValue]) {
            const flashSaleDetails = skillGame[oneValue]?.sale_details;
            const saleDetails = this.gameDatasService.getFlashSaleData(flashSaleDetails);
            this.flashSalesExpiryDate = saleDetails.flash_sale_date;
            this.salesCost = this.authService?.flashSaleEnabled ? saleDetails?.sales_cost : '';
            this.flashSaleEnabled = this.gameDatasService.isFlashSaleToday(this.flashSalesExpiryDate);

            const inputData = {
              myString: skillGame[zeroValue]
                ? { ...skillGame[oneValue], ...skillGame[zeroValue] }
                : skillGame[oneValue],
              currency: preferredCurrency,
            };
            return from(this.authService.getDvResolvedJson(inputData)).pipe(
              map((res: Data) => {
                const dvResolvedData = res?.data;
                const data = { ...dvResolvedData, ...skillGame[zeroValue] };
                if (dvResolvedData?.completed_chapter_content || dvResolvedData?.your_progress_chapter_details) {
                  this.completedChapters = dvResolvedData.completed_chapter_content;
                  const chapterLength = dvResolvedData?.your_progress_chapter_details?.length;
                  const completedChapterLength = dvResolvedData?.completed_chapter_content?.length;

                  if (chapterLength || completedChapterLength) {
                    this.chapterPrize = {} as Data;

                    from(
                      this.authService.getDvResolvedJson({
                        myString: skillGame[oneValue],
                        currency: preferredCurrency,
                      })
                    )
                      .pipe(takeUntil(this.destroy$))
                      .subscribe({
                        next: (res: Data) => {
                          const globalResolvedData = res?.data;
                          globalResolvedData?.your_progress_chapter_details?.forEach((eachChapter: Data) => {
                            const currentChapter =
                              globalResolvedData.your_progress_chapter_details[eachChapter?.chapter_id - 1];
                            const prize =
                              currentChapter?.chapter_prize?.int_prize?.[this.languageCode] ||
                              currentChapter?.chapter_prize?.prize;
                            const chapterId = eachChapter?.chapter_id;
                            if (prize) {
                              this.chapterPrize[chapterId] = {
                                chapter_prize: {
                                  int_prize: prize,
                                },
                              };
                            }
                          });
                        },
                        error: (err) => {
                          
                        },
                      });
                  }

                  this.inProgressChapters = dvResolvedData?.your_progress_chapter_details;
                }

                const gamePopupDetails =
                  dvResolvedData?.int_learn_more_details?.[this.languageCode] ||
                  dvResolvedData?.learn_more_details ||
                  dvResolvedData?.int_game_popup_details?.[this.languageCode] ||
                  dvResolvedData?.game_popup_details;

                if (gamePopupDetails) {
                  data.game_popup_details = gamePopupDetails;
                }

                const learn_more_rules =
                  dvResolvedData?.int_learn_more_rules?.[this.languageCode] ||
                  dvResolvedData?.learn_more_rules;

                if (learn_more_rules) {
                  data.learn_more_rules = learn_more_rules;
                }

                if (data?.int_total_prize) {
                  this.gameTotalPrize =
                    data.int_total_prize[this.languageCode] ?? data?.total_prize;
                } else {
                  const codeMap = {
                    CAD: 1,
                    EUR: 353,
                    GBP: 44,
                  };
                  const code = codeMap[this.preferredCurrency as keyof typeof codeMap] ?? 44;
                  this.gameTotalPrize = data?.country_code_data?.[code]?.total_prize ?? '';
                }

                this.gameService.setCurrentGameOrientationStatus(data?.orientation);
                return data;
              }),
              catchError((err) => {
                
                return of(err);
              })
            );
          } else {
            
            return of({});
          }
        }),
        catchError((err) => {
          
          return of({});
        })
      );

  completedChapters: GameChapterBadge[] = [];
  inProgressChapters: GameChapterBadge[] = [];
  chapterPrize: Data;
  userGameRestriction: boolean;
  userId: string;
  constructor(
    private location: Location,
    private gameService: GamesRepositoryService,
    private route: ActivatedRoute,
    private analytics: AngularFireAnalytics,
    public dialog: MatDialog,
    public translate: TranslateService,
    private GTMSegment: GTMSegmentService,
    private router: Router,
    private gamesFeedService: GamesFeedSampleService,
    private gameDatasService: GameDatasService,
  ) {
    super()
    this.hidePrizeHeading = 633;
    this.languageCode = this.authService?.selectedlanguage$?.value || CommonVariable.preferredLanguage || "en";
    translate.use(this.languageCode);
  }

  getPrize(game: Data) {
    let prize = 0;
    if (this.inProgressChapters && this.inProgressChapters["game_prize"])
      prize = this.inProgressChapters["game_prize"]?.find((f) => f?.chapter_id === game?.chapter_id)?.chapter_prize
        ?.country_code_data["44"];
    return prize;
  }

  async playGame(game: Data) {
    const gamePrice = game?.sale_price || game?.game_price;
    if (!this.userGameRestriction) {
      this.isButtonDisabled = true;
      const userGamePoint = this.gamesFeedService.userGamePoints;
      const hasSufficientPoints = await this.gamesFeedService.checkUserGamePoints(userGamePoint, gamePrice);
      if (!hasSufficientPoints) return;
      this.GTMSegment?.HotJarEvent("user_played_game");
      if (!isMobile.any() && game.orientation == "landscape") {
        this.dialog.open(WindowsGamePlayPopupComponent);
        return null;
      }
      game.document = "/api_post_request/customer_game_token_authentication";
      game.from_page = "skill-game";
      game.user_game_restriction = this.userGameRestriction;
      this.playGameBase(game)
    } else {
      this.isButtonDisabled = false;
      // this.authGamesService?.callDedicatedInboxMessage();
      this.authService.getConstantsMsg("1148")
        .then((translatedMsg) => {
          const message = "You have reached the game play limit";
          const defaultMsg = {
            message: {
              line_1: message,
              line_2: "",
            },
            style: {
              icon: "tick circle error.svg",
              type: "Error",
            },
          };
          this.authService.showToastMessage(translatedMsg, defaultMsg);
        })
        .catch((error) => {
          return error;
        });
    }
  }

  async ngOnInit() {
    super.ngOnInit()
    await this.authService
      ?.getUid("SkillGameComponent")
      ?.then((uid) => {
        this.userId = uid;
        this.analytics.logEvent("skill_game_screen", {
          userId: this.userId
        });
      })
      .catch((error) => {
        return error;
      });
    const navigation = this.router.getCurrentNavigation();
    this.flashCost = navigation?.extras.state;
  }

  goBack() {
    this.location.back();
  }

  ionViewWillLeave() {
    this.userDataSubscription?.unsubscribe();
  }
}
